/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AsyncThunk, SerializedError } from '@reduxjs/toolkit';
import _ from 'lodash';
import { useEffect, useState, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { shallowEqual } from 'react-redux';
import { useMount } from 'react-use';

import { selectPlan } from '@ibm-watson/shared/modules/ia/store/instance/selectors';
import { PLAN_NAMES } from '@ibm-watson/shared/modules/ia/store/plan/constants';
import { useAppDispatch, useAppSelector } from '@ibm-watson/shared/store';
import {
  SpeechInstance,
  SpeechToTextInstance,
  TextToSpeechInstance
} from '@ibm-watson/shared/ts/speech';
import {
  AsyncAppThunk,
  CreateAsyncThunk,
  RootState
} from '@ibm-watson/shared/ts/state';
import { FIELDS } from '../components/phone/schema';
import {
  hasPremiumSpeech,
  isPremiumSpeech
} from '../helpers/speech-plan-helpers';
import {
  listSpeechToTextInstancesIfRequired,
  loadSpeechToTextInstance,
  listTextToSpeechInstancesIfRequired,
  loadTextToSpeechInstance
} from '../store/voice/actions';
import {
  selectCurrentTextToSpeechInstance,
  selectTextToSpeechInstances,
  selectTextToSpeechInstanceIsLoading,
  selectTextToSpeechInstanceLoaded,
  selectTextToSpeechListIsLoading,
  selectTextToSpeechInstanceLoadFailed,
  selectTextToSpeechListLoadFailed,
  selectTextToSpeechCreateError,
  selectCurrentSpeechToTextInstance,
  selectSpeechToTextInstances,
  selectSpeechToTextListLoadFailed,
  selectSpeechToTextInstanceIsLoading,
  selectSpeechToTextListIsLoading,
  selectSpeechToTextInstanceLoaded,
  selectSpeechToTextInstanceLoadFailed,
  selectSpeechToTextCreateError
} from '../store/voice/selectors';
import { useFormHelpers } from './useFormHelpers';

interface UseSpeechCommon<T> {
  selectListIsLoading: (state: RootState) => boolean;
  selectListLoadFailed: (state: RootState) => boolean;
  selectInstanceIsLoading: (state: RootState) => boolean;
  selectInstanceLoaded: (state: RootState) => boolean;
  selectCurrentInstance: (state: RootState) => SpeechInstance;
  selectCreateError: (state: RootState) => SerializedError | null;
  selectInstanceLoadFailed: (state: RootState) => boolean;
  selectInstances: (state: RootState) => T[];
  listInstancesIfRequired: () => AsyncAppThunk<any>;
  loadInstance: AsyncThunk<T, string, CreateAsyncThunk>;
  instanceIdField: string;
  instanceUrlField: string;
  instanceApiKeyField: string;
}
/**
 * Common hook to return selectors/loaders depending on
 * args passed
 */
const useSpeechCommon = <T>({
  selectListIsLoading,
  selectListLoadFailed,
  selectInstanceIsLoading,
  selectInstanceLoaded,
  selectCurrentInstance,
  selectCreateError,
  selectInstances,
  listInstancesIfRequired,
  loadInstance,
  instanceIdField,
  instanceUrlField,
  instanceApiKeyField
}: UseSpeechCommon<T>) => {
  const dispatch = useAppDispatch();

  const planName = useAppSelector(selectPlan);

  const formMethods = useFormContext();
  const { updateFieldValue } = useFormHelpers(formMethods);

  const [createRequired, setCreateRequired] = useState(false);
  const [enterpriseCreateRequired, setEnterpriseCreateRequired] =
    useState(false);

  const [showPremiumWarning, setShowPremiumWarning] = useState(false);

  const instances = useAppSelector(selectInstances, shallowEqual);
  const loadingInstances = useAppSelector(selectListIsLoading);
  const currentInstance = useAppSelector(selectCurrentInstance);
  const loadingInstance = useAppSelector(selectInstanceIsLoading);
  const loadingInstancesFailed = useAppSelector(selectListLoadFailed);
  const loadingInstanceSucceeded = useAppSelector(selectInstanceLoaded);
  const createError = useAppSelector(selectCreateError);

  /**
   *  On mount, load all STT or TTS instances.
   */
  useMount(() => {
    dispatch(listInstancesIfRequired());
  });

  useEffect(() => {
    if (currentInstance && !loadingInstance) {
      setShowPremiumWarning(
        planName === PLAN_NAMES.ENTERPRISE_ISOLATED &&
          !isPremiumSpeech(currentInstance)
      );
    }
  }, [currentInstance, loadingInstance, planName, showPremiumWarning]);

  /**
   * Check if we need to create the instance.
   *
   * We need to create the instance, if:
   * - we are not loading the instance, AND
   * - there are no existing instances
   *
   * Additionally, we create the instance on enterprise, if:
   * - we need to create the instance generally, AND
   * - the instance is in the isolated enterprise plan, AND
   * - there isn't already a premium speech instance
   */
  useEffect(() => {
    if (
      !loadingInstances &&
      !loadingInstancesFailed &&
      instances?.length === 0
    ) {
      setCreateRequired(true);

      if (planName === PLAN_NAMES.ENTERPRISE_ISOLATED && !hasPremiumSpeech) {
        setEnterpriseCreateRequired(true);
      }
    } else {
      setCreateRequired(false);
    }
  }, [instances, loadingInstances, loadingInstancesFailed, planName]);

  /**
   * Process when a STT/TTS instance is selected, and update the
   * current instance's ID, api key, and URL fields.
   */
  const handleSelect = useCallback(
    async (id: string) => {
      // `.payload` is used here instead of `.unwrap()` because for
      // some reason TypeScript is not playng will with the generic
      // T typing defined in the interface `UseSpeechCommon<T>`
      const loadedInstance = (await dispatch(loadInstance(id)))
        .payload as SpeechInstance;

      // Only load the instance if credentials are supplied
      if (!_.isEmpty(loadedInstance?.credentials)) {
        updateFieldValue(instanceIdField, id);
        updateFieldValue(
          instanceApiKeyField,
          loadedInstance.credentials.apikey
        );
        updateFieldValue(instanceUrlField, loadedInstance.credentials.url);
      }
    },
    [
      dispatch,
      instanceApiKeyField,
      instanceIdField,
      instanceUrlField,
      loadInstance,
      updateFieldValue
    ]
  );

  return {
    instances,
    createRequired,
    createError,
    showPremiumWarning,
    currentInstance,
    enterpriseCreateRequired,
    loadingInstanceSucceeded,
    loadingInstances,
    handleSelect
  };
};

/**
 * Hook to setup all the selectors/loaders for TTS
 */
export const useTextToSpeech = () => {
  return useSpeechCommon<TextToSpeechInstance>({
    selectListIsLoading: selectTextToSpeechListIsLoading,
    selectListLoadFailed: selectTextToSpeechListLoadFailed,
    selectInstanceIsLoading: selectTextToSpeechInstanceIsLoading,
    selectInstanceLoaded: selectTextToSpeechInstanceLoaded,
    selectCurrentInstance: selectCurrentTextToSpeechInstance,
    selectInstanceLoadFailed: selectTextToSpeechInstanceLoadFailed,
    selectInstances: selectTextToSpeechInstances,
    selectCreateError: selectTextToSpeechCreateError,
    listInstancesIfRequired: listTextToSpeechInstancesIfRequired,
    loadInstance: loadTextToSpeechInstance,
    instanceIdField: FIELDS.TTS.INSTANCE_ID,
    instanceUrlField: FIELDS.TTS.URL,
    instanceApiKeyField: FIELDS.TTS.APIKEY
  });
};

/**
 * Hook to setup all the selectors/loaders for STT
 */
export const useSpeechToText = () => {
  return useSpeechCommon<SpeechToTextInstance>({
    selectListIsLoading: selectSpeechToTextListIsLoading,
    selectInstanceIsLoading: selectSpeechToTextInstanceIsLoading,
    selectInstanceLoaded: selectSpeechToTextInstanceLoaded,
    selectListLoadFailed: selectSpeechToTextListLoadFailed,
    selectCurrentInstance: selectCurrentSpeechToTextInstance,
    selectInstanceLoadFailed: selectSpeechToTextInstanceLoadFailed,
    selectInstances: selectSpeechToTextInstances,
    selectCreateError: selectSpeechToTextCreateError,
    listInstancesIfRequired: listSpeechToTextInstancesIfRequired,
    loadInstance: loadSpeechToTextInstance,
    instanceIdField: FIELDS.STT.INSTANCE_ID,
    instanceUrlField: FIELDS.STT.URL,
    instanceApiKeyField: FIELDS.STT.APIKEY
  });
};
